import { debounce } from 'lodash-es';
import { makeTDAClient, makeTDAClientProtocol } from '@barracuda/tda-api-client';
import { makeVRMClient, makeVRMClientProtocol } from '@barracuda/vrm-api-client';
import { isTokenCloseToExpired, isTokenExpired, sopClient } from './Auth';
const debug = process.env.REACT_APP_MODE === 'dev';
const VALIDATE_TOKEN_SECONDS = 10;
const ACTIVITY_TIMEOUT_DEBOUNCE_SECONDS = 30;
const ACTIVITY_TIMEOUT_MINUTES = 10;
const ACTIVITY_EVENTS = ['mousedown', 'mousemove', 'keydown', 'scroll', 'touchstart'];
let testLicenseId;
let testToken;
let refreshInProgress = false;
let lastActiveAt = Date.now();
export const markUserPresence = async (e) => {
    lastActiveAt = Date.now();
    if (debug) {
        if (e?.target instanceof HTMLElement) {
            console.log(`TestAuth.markUserPresence(${e.type})`, e.target.nodeName);
        }
        else {
            console.log(`TestAuth.markUserPresence(${e?.type})`);
        }
    }
};
const markUserPresenceDebounced = debounce(markUserPresence, ACTIVITY_TIMEOUT_DEBOUNCE_SECONDS * 1000, { leading: true, maxWait: ACTIVITY_TIMEOUT_DEBOUNCE_SECONDS * 1000 });
ACTIVITY_EVENTS.forEach((eventName) => {
    document.addEventListener(eventName, markUserPresenceDebounced, {
        passive: true
    });
});
export const tdaClient = makeTDAClient(makeTDAClientProtocol({
    baseUrl: process.env.REACT_APP_PESC_TEST_DELIVERY_URL
}));
export const vrmClient = makeVRMClient(makeVRMClientProtocol({
    baseUrl: process.env.REACT_APP_PESC_REMOTE_MONITORING_URL
}));
const dispatchLocalStorageEvent = (key, oldValue, newValue) => {
    window.dispatchEvent(new StorageEvent('storage', {
        storageArea: window.localStorage,
        key,
        oldValue,
        newValue
    }));
};
// at first run it is not yet stored in sop client
export const getTestToken = () => {
    return testToken || localStorage.getItem('testToken') || undefined;
};
// at first run it is not yet stored in sop client
export const getTestLicenseId = () => {
    if (testLicenseId) {
        return testLicenseId;
    }
    let licenseIdAsString = localStorage.getItem('testLicenseId');
    if (licenseIdAsString) {
        return parseInt(licenseIdAsString, 10);
    }
    return undefined;
};
export const removeTestToken = () => {
    setTestToken();
};
export const loadExistingTestToken = () => {
    setTestToken(getTestLicenseId(), getTestToken());
};
export const setTestToken = (licenseId, token) => {
    if (debug) {
        console.log(`TestAuth.setTestToken(${licenseId}, ${token})`);
    }
    const oldToken = testToken;
    if (oldToken !== token) {
        testToken = token;
        testLicenseId = licenseId;
        if (token) {
            tdaClient.session.setToken(token);
            vrmClient.session.setToken(token);
            localStorage.setItem('testToken', token);
            localStorage.setItem('testLicenseId', licenseId + '');
        }
        else {
            tdaClient.session.deleteToken();
            vrmClient.session.deleteToken();
            localStorage.removeItem('testToken');
            localStorage.removeItem('testLicenseId');
        }
        dispatchLocalStorageEvent('testToken', oldToken, token || undefined);
    }
};
const refreshToken = async () => {
    try {
        const { token } = await sopClient.refreshLicense(testLicenseId + '');
        setTestToken(testLicenseId, token);
    }
    catch (error) {
        console.log('There was an error during refreshing token', error);
        removeTestToken();
    }
};
let testTokenCheckTimeout;
const checkTestToken = () => {
    clearTimeout(testTokenCheckTimeout);
    testTokenCheckTimeout = setTimeout(() => checkTestToken(), VALIDATE_TOKEN_SECONDS * 1000);
    const testToken = getTestToken();
    if (testToken) {
        if (isTokenExpired(testToken)) {
            removeTestToken();
            return;
        }
        if (!refreshInProgress && isTokenCloseToExpired(testToken)) {
            const now = Date.now();
            const howManyMinutesAgoWasActive = (now - lastActiveAt) / (1000 * 60);
            if (howManyMinutesAgoWasActive < ACTIVITY_TIMEOUT_MINUTES) {
                refreshInProgress = true;
                refreshToken().finally(() => (refreshInProgress = false));
            }
        }
    }
};
export const addTokenUpdatedListener = (callback) => {
    let listener = (e) => {
        if (e.storageArea === window.localStorage && e.key === 'testToken') {
            if (e.oldValue !== e.newValue) {
                const token = e.newValue ? e.newValue : undefined;
                if (debug) {
                    console.log('TestAuth.tokenUpdatedEvent', token, e);
                }
                tdaClient.session.setToken(token);
                vrmClient.session.setToken(token);
                callback?.(token);
            }
        }
    };
    window.addEventListener('storage', listener);
    checkTestToken();
    return () => {
        window.removeEventListener('storage', listener);
        clearTimeout(testTokenCheckTimeout);
    };
};
//# sourceMappingURL=TestAuth.js.map